import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import { HomePage } from './components/HomePage.tsx'
import { ExtensionHome } from './components/ExtensionHome.tsx'
import { BlogsPage } from './components/BlogsPage.tsx'
import { SubscribeClipboard } from './components/SubscribeClipboard.tsx'
import { ClipboardActivated } from './components/ClipboardActivated.tsx'
import { ClipboardRestore } from './components/ClipboardRestore.tsx'
import { initAnalytics } from './lib/mixpanel'

// Initialize Sentry for error tracking
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    // Tracing
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost'],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Logs
    enableLogs: true,
    environment: import.meta.env.MODE,
  })
}

// Initialize Mixpanel analytics
initAnalytics()

// Request persistent storage to prevent IndexedDB eviction
if (navigator.storage?.persist) {
  navigator.storage.persist().catch(() => {
    // Silently fail - not critical
  })
}

const path = window.location.pathname.replace(/\/$/, '') || '/'
const isExtensionHome = path === '/home'
const isEditorLanding = path === '/editor'
const isBlogsPage = path === '/blogs'
const isSubscribe = path === '/subscribe'
const isClipboardActivated = path === '/extension/activated'
const isClipboardRestore = path === '/extension/restore'

const oauthParams = new URLSearchParams(window.location.search)
const isOAuthCallback =
  oauthParams.has('code') ||
  oauthParams.has('error') ||
  window.location.hash.includes('access_token') ||
  window.location.hash.includes('error')

// Always mount the editor for OAuth returns. Marketing routes don't run
// useAuth, so rendering them here would leave Google sign-in unfinished.
const showEditor =
  isOAuthCallback ||
  !(
    isExtensionHome ||
    isEditorLanding ||
    isBlogsPage ||
    isSubscribe ||
    isClipboardActivated ||
    isClipboardRestore
  )

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {showEditor ? (
      <Sentry.ErrorBoundary
        fallback={({ error }) => (
          <div className="min-h-screen flex items-center justify-center bg-yellow-50 p-4">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h1>
              <p className="text-gray-600 mb-4">
                {(error as Error)?.message || 'An unexpected error occurred'}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 font-medium rounded-md"
              >
                Reload App
              </button>
            </div>
          </div>
        )}
      >
        <App />
      </Sentry.ErrorBoundary>
    ) : isExtensionHome ? (
      <ExtensionHome />
    ) : isEditorLanding ? (
      <HomePage />
    ) : isBlogsPage ? (
      <BlogsPage />
    ) : isSubscribe ? (
      <SubscribeClipboard />
    ) : isClipboardActivated ? (
      <ClipboardActivated />
    ) : (
      <ClipboardRestore />
    )}
  </StrictMode>,
)
